import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ApiError } from "../api/client";
import { workspacesApi } from "../api/endpoints";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(email, password);
      // Post-login redirect discipline:
      //   • explicit `returnTo` query param wins (deep-link recovery)
      //   • else: if the user has ≥1 workspace membership, land on the
      //     first workspace's dashboard
      //   • else: land on `/no-workspace` — an AUTHENTICATED page that
      //     tells the user (or platform admin) their state. Never route
      //     to a public marketing surface after a successful login —
      //     public pages have hardcoded "Sign in" headers that ignore
      //     auth state and create the illusion of a failed login.
      let target = returnTo ? decodeURIComponent(returnTo) : null;
      if (!target) {
        try {
          const workspaces = await workspacesApi.list();
          if (workspaces && workspaces[0]) {
            target = `/w/${workspaces[0].id}/dashboard`;
          } else {
            target = "/no-workspace";
          }
        } catch {
          target = "/no-workspace";
        }
      }
      navigate(target, { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        setErr(e);
      } else {
        setErr(new ApiError("UNEXPECTED", e.message || "Unexpected error"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-slate-100 p-10">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded bg-white text-slate-900 text-sm font-bold tracking-widest">
            RA
          </span>
          <div>
            <div className="text-lg font-semibold">RecoveryAssure</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">
              Deterministic Business Continuity
            </div>
          </div>
        </div>
        <div className="max-w-md space-y-6">
          <h1 className="text-3xl font-semibold leading-tight">
            Every finding, every number, traceable to a source or a rule.
          </h1>
          <p className="text-sm text-slate-300 leading-relaxed">
            RecoveryAssure evaluates whether your recovery time objectives are
            actually feasible — using a deterministic engine, not a black-box
            score. Import your BIA data, and every conclusion arrives with a
            citation.
          </p>
          <ul className="text-sm text-slate-300 space-y-2 border-l border-slate-700 pl-4">
            <li>Engine-first. Rule versions are pinned per run.</li>
            <li>Provenance for every derived value.</li>
            <li>Tenant-strict. Workspace boundaries never leak.</li>
          </ul>
        </div>
        <div className="text-[11px] text-slate-500">
          © RecoveryAssure ·{" "}
          <Link to="/methodology" className="underline">
            Methodology
          </Link>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded bg-slate-900 text-white text-xs font-bold tracking-widest">
              RA
            </span>
            <div className="text-sm font-semibold text-slate-900">
              RecoveryAssure
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Sign in</h2>
            <p className="mt-1 text-sm text-slate-500">
              Use the credentials your workspace admin provided.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-medium uppercase tracking-wider text-slate-600 mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-xs font-medium uppercase tracking-wider text-slate-600 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
              />
            </div>
            {err && (
              <div
                role="alert"
                data-testid="login-error"
                className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-center gap-2"
              >
                <code className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-mono">
                  {err.code}
                </code>
                <span>
                  {err.code === "INVALID_CREDENTIALS"
                    ? "Invalid credentials"
                    : err.message}
                </span>
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full rounded bg-slate-900 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="text-xs text-slate-500 text-center">
            No self-service registration. Contact your workspace admin.
          </p>
        </div>
      </div>
    </div>
  );
}
